import { PixelCrop } from "react-image-crop";

const TO_RADIANS = Math.PI / 180;

export function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0,
  addBorder = false,
  previews: {
    canvas: HTMLCanvasElement | null;
    size: {
      width: number;
      height: number;
    };
  }[] = []
) {
  // Draw the main canvas (this determines the crop)
  drawImage({ image, canvas, crop, scale, rotate, addBorder });

  // Draw the same crop to each preview canvas, scaled to cover
  previews.forEach((preview) => {
    if (!preview.canvas) return;

    drawImage({
      image,
      canvas: preview.canvas,
      crop,
      scale,
      rotate,
      addBorder,
      previewSize: preview.size,
    });
  });
}

const drawImage = ({
  image,
  canvas,
  crop,
  scale = 1,
  rotate = 0,
  addBorder = false,
  previewSize,
}: {
  image: HTMLImageElement;
  canvas: HTMLCanvasElement;
  crop: PixelCrop;
  scale?: number;
  rotate?: number;
  addBorder?: boolean;
  previewSize?: {
    width: number;
    height: number;
  };
}) => {
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  // Calculate original crop dimensions
  const originalCropWidth = crop.width * scaleX;
  const originalCropHeight = crop.height * scaleY;
  const originalAspectRatio = originalCropWidth / originalCropHeight;

  // Set canvas dimensions
  if (previewSize) {
    canvas.width = Math.floor(previewSize.width * pixelRatio);
    canvas.height = Math.floor(previewSize.height * pixelRatio);
    
    // Calculate scale to cover the preview canvas while maintaining crop aspect ratio
    const previewAspectRatio = previewSize.width / previewSize.height;
    let coverScale;
    
    if (originalAspectRatio > previewAspectRatio) {
      // Original is wider, scale to fit height and crop width
      coverScale = previewSize.height / originalCropHeight;
    } else {
      // Original is taller, scale to fit width and crop height
      coverScale = previewSize.width / originalCropWidth;
    }
    
    // Apply the cover scale
    ctx.scale(pixelRatio * coverScale, pixelRatio * coverScale);
    
    // Center the scaled crop in the canvas
    const scaledCropWidth = originalCropWidth * coverScale;
    const scaledCropHeight = originalCropHeight * coverScale;
    const offsetX = (previewSize.width - scaledCropWidth) / (2 * coverScale);
    const offsetY = (previewSize.height - scaledCropHeight) / (2 * coverScale);
    
    ctx.translate(offsetX, offsetY);
  } else {
    // For main canvas, use original logic
    canvas.width = Math.floor(originalCropWidth * pixelRatio);
    canvas.height = Math.floor(originalCropHeight * pixelRatio);
    ctx.scale(pixelRatio, pixelRatio);
  }

  ctx.imageSmoothingQuality = "high";

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // Apply transformations
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  if (addBorder) {
    const borderAmount = 180;
    ctx.clearRect(0, cropY, canvas.width, borderAmount);
    ctx.clearRect(
      0,
      cropY + originalCropHeight - borderAmount,
      canvas.width,
      borderAmount
    );
    ctx.clearRect(cropX, 0, borderAmount, canvas.height);
    ctx.clearRect(
      cropX + originalCropWidth - borderAmount,
      0,
      borderAmount,
      canvas.height
    );
  }

  ctx.restore();
};
